import React, { useState, useEffect } from 'react';
import {
  getGoggleModels,
  createOrder,
  GoggleModel,
  getGlassOptions,
  getBandOptions,
  getProductTypes,
  GlassOption,
  BandOption,
} from '../services/orders';
import { getDeliveryTimes, DeliveryTimes } from '../services/public';
import './OrderForm.css';

const OrderForm: React.FC = () => {
  const [goggleModels, setGoggleModels] = useState<GoggleModel[]>([]);
  const [glassOptions, setGlassOptions] = useState<GlassOption[]>([]);
  const [bandOptions, setBandOptions] = useState<BandOption[]>([]);
  const [availableProductTypes, setAvailableProductTypes] = useState<('CLIP' | 'COMPLETE_GLASSES')[]>([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedGlass, setSelectedGlass] = useState('');
  const [selectedBand, setSelectedBand] = useState('');
  const [productType, setProductType] = useState<'CLIP' | 'COMPLETE_GLASSES'>('CLIP');
  const [deliveryType, setDeliveryType] = useState<'SHOP' | 'CUSTOMER'>('SHOP');
  const [customerAddress, setCustomerAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [internalOrderNumber, setInternalOrderNumber] = useState('');
  const [prescriptionImage, setPrescriptionImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [priceInfo, setPriceInfo] = useState<{
    basePrice: number;
    discountPercent: number;
    finalPrice: number;
  } | null>(null);
  const [deliveryTimes, setDeliveryTimes] = useState<DeliveryTimes | null>(null);
  // Brillenpass-Werte
  const [leftEyePlano, setLeftEyePlano] = useState<boolean>(false);
  const [leftEyeSph, setLeftEyeSph] = useState<string>('');
  const [leftEyeCyl, setLeftEyeCyl] = useState<string>('');
  const [leftEyeA, setLeftEyeA] = useState<string>('');
  const [rightEyePlano, setRightEyePlano] = useState<boolean>(false);
  const [rightEyeSph, setRightEyeSph] = useState<string>('');
  const [rightEyeCyl, setRightEyeCyl] = useState<string>('');
  const [rightEyeA, setRightEyeA] = useState<string>('');

  useEffect(() => {
    loadGoggleModels();
    loadDeliveryTimes();
  }, []);

  // Load model-specific options when model changes
  useEffect(() => {
    if (selectedModel) {
      loadModelOptions(selectedModel);
    } else {
      // Clear options when no model is selected
      setGlassOptions([]);
      setBandOptions([]);
      setAvailableProductTypes([]);
      setSelectedGlass('');
      setSelectedBand('');
      setProductType('CLIP');
    }
  }, [selectedModel]);

  // Clear glass and band when switching to CLIP
  useEffect(() => {
    if (productType === 'CLIP') {
      setSelectedGlass('');
      setSelectedBand('');
    }
  }, [productType]);

  const loadDeliveryTimes = async () => {
    try {
      const times = await getDeliveryTimes();
      setDeliveryTimes(times);
    } catch (err) {
      console.error('Error loading delivery times:', err);
    }
  };

  const loadGoggleModels = async () => {
    try {
      const models = await getGoggleModels();
      setGoggleModels(models);
    } catch (err) {
      setError('Fehler beim Laden der Modelle');
    }
  };

  const loadModelOptions = async (modelId: string) => {
    try {
      // Load all model-specific options in parallel
      const [glassOpts, bandOpts, productTypes] = await Promise.all([
        getGlassOptions(modelId),
        getBandOptions(modelId),
        getProductTypes(modelId),
      ]);

      setGlassOptions(glassOpts);
      setBandOptions(bandOpts);
      setAvailableProductTypes(productTypes);

      // Reset selections if current product type is not available for this model
      if (productTypes.length > 0 && !productTypes.includes(productType)) {
        setProductType(productTypes[0]);
        setSelectedGlass('');
        setSelectedBand('');
      }

      // Clear glass/band selections when model changes
      setSelectedGlass('');
      setSelectedBand('');
    } catch (err: any) {
      console.error('Error loading model options:', err);
      setError(
        'Fehler beim Laden der Modell-Optionen: ' + (err.response?.data?.error || err.message)
      );
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPrescriptionImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setOrderNumber('');

    if (!selectedModel) {
      setError('Bitte wählen Sie ein Modell aus');
      return;
    }

    if (!productType) {
      setError('Bitte wählen Sie einen Produkttyp aus');
      return;
    }

    // Only require glass and band for COMPLETE_GLASSES
    if (productType === 'COMPLETE_GLASSES') {
      if (!selectedGlass) {
        setError('Bitte wählen Sie ein Glas aus');
        return;
      }

      if (!selectedBand) {
        setError('Bitte wählen Sie ein Band aus');
        return;
      }
    }

    const hasImage = !!prescriptionImage;
    const leftComplete =
      leftEyePlano ||
      (leftEyeSph.trim() !== '' && leftEyeCyl.trim() !== '' && leftEyeA.trim() !== '');
    const rightComplete =
      rightEyePlano ||
      (rightEyeSph.trim() !== '' && rightEyeCyl.trim() !== '' && rightEyeA.trim() !== '');
    const prescriptionValuesComplete = leftComplete && rightComplete;

    if (!hasImage && !prescriptionValuesComplete) {
      setError('Bitte entweder ein Rezeptbild hochladen oder alle Brillenpasswerte ausfüllen');
      return;
    }

    if (deliveryType === 'CUSTOMER' && !customerAddress.trim()) {
      setError('Bitte geben Sie die Kundenadresse an');
      return;
    }

    // Validate notes length
    if (notes && notes.length > 2000) {
      setError('Notizen dürfen maximal 2000 Zeichen lang sein');
      return;
    }

    // Parse and validate prescription values if provided
    const parseFloatOrNull = (value: string): number | null => {
      if (!value || value.trim() === '') return null;
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parseFloat(parsed.toFixed(2));
    };

    const parseIntOrNull = (value: string): number | null => {
      if (!value || value.trim() === '') return null;
      const parsed = parseInt(value, 10);
      if (isNaN(parsed)) return null;
      if (parsed < 0 || parsed > 180) {
        setError('Achse (A) muss zwischen 0 und 180 Grad liegen');
        return null;
      }
      return parsed;
    };

    const prescriptionValues = {
      leftEyeSph: leftEyePlano ? 0.00 : parseFloatOrNull(leftEyeSph),
      leftEyeCyl: leftEyePlano ? 0.00 : parseFloatOrNull(leftEyeCyl),
      leftEyeA: leftEyePlano ? 0 : parseIntOrNull(leftEyeA),
      rightEyeSph: rightEyePlano ? 0.00 : parseFloatOrNull(rightEyeSph),
      rightEyeCyl: rightEyePlano ? 0.00 : parseFloatOrNull(rightEyeCyl),
      rightEyeA: rightEyePlano ? 0 : parseIntOrNull(rightEyeA),
    };

    // Check if any prescription value is invalid (parsing failed)
    if (leftEyeSph && prescriptionValues.leftEyeSph === null) {
      setError('Ungültiger Wert für linkes Auge Sph');
      return;
    }
    if (leftEyeCyl && prescriptionValues.leftEyeCyl === null) {
      setError('Ungültiger Wert für linkes Auge Cyl');
      return;
    }
    if (leftEyeA && prescriptionValues.leftEyeA === null) {
      setError('Ungültiger Wert für linkes Auge A (muss zwischen 0 und 180 sein)');
      return;
    }
    if (rightEyeSph && prescriptionValues.rightEyeSph === null) {
      setError('Ungültiger Wert für rechtes Auge Sph');
      return;
    }
    if (rightEyeCyl && prescriptionValues.rightEyeCyl === null) {
      setError('Ungültiger Wert für rechtes Auge Cyl');
      return;
    }
    if (rightEyeA && prescriptionValues.rightEyeA === null) {
      setError('Ungültiger Wert für rechtes Auge A (muss zwischen 0 und 180 sein)');
      return;
    }
    setLoading(true);

    try {
      const response = await createOrder({
        goggleModelId: selectedModel,
        glassType: productType === 'COMPLETE_GLASSES' ? selectedGlass : '',
        bandType: productType === 'COMPLETE_GLASSES' ? selectedBand : '',
        productType,
        deliveryType,
        customerAddress: deliveryType === 'CUSTOMER' ? customerAddress : undefined,
        notes: notes.trim() || undefined,
        internalOrderNumber: internalOrderNumber.trim() || undefined,
        prescriptionImage,
        ...prescriptionValues,
      });

      setSuccess('Bestellung erfolgreich erstellt!');
      setOrderNumber(response.order.orderNumber);
      setPriceInfo({
        basePrice: response.order.basePrice,
        discountPercent: response.order.discountPercent,
        finalPrice: response.order.finalPrice,
      });

      // Reset form
      setSelectedModel('');
      setProductType('CLIP');
      setDeliveryType('SHOP');
      setSelectedGlass('');
      setSelectedBand('');
      setCustomerAddress('');
      setNotes('');
      setInternalOrderNumber('');
      setPrescriptionImage(null);
      setImagePreview(null);
      setPriceInfo(null);
      setLeftEyePlano(false);
      setLeftEyeSph('');
      setLeftEyeCyl('');
      setLeftEyeA('');
      setRightEyePlano(false);
      setRightEyeSph('');
      setRightEyeCyl('');
      setRightEyeA('');
      const fileInput = document.getElementById('prescriptionImage') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err: any) {
      console.error('Order creation error:', err);
      const errorMessage =
        err.response?.data?.error || err.message || 'Fehler beim Erstellen der Bestellung';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <h1>Neue Bestellung</h1>
      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="internalOrderNumber">Vermerk (optional)</label>
            <input
              id="internalOrderNumber"
              type="text"
              value={internalOrderNumber}
              onChange={e => setInternalOrderNumber(e.target.value)}
              placeholder="z.B. Kunde: Max Mustermann oder Auftrag: A-2024-001"
              disabled={loading}
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label htmlFor="goggleModel">Skibrillen-Modell *</label>
            <select
              id="goggleModel"
              value={selectedModel}
              onChange={e => {
                setSelectedModel(e.target.value);
                // Options will be loaded via useEffect
              }}
              required
              disabled={loading}
            >
              <option value="">Bitte wählen...</option>
              {goggleModels.map(model => (
                <option key={model.id} value={model.id}>
                  {model.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="productType">Produkttyp *</label>
            <select
              id="productType"
              value={productType}
              onChange={e => setProductType(e.target.value as 'CLIP' | 'COMPLETE_GLASSES')}
              required
              disabled={loading || !selectedModel || availableProductTypes.length === 0}
            >
              {availableProductTypes.length === 0 ? (
                <option value="">Bitte zuerst Modell wählen...</option>
              ) : (
                availableProductTypes.map(type => (
                  <option key={type} value={type}>
                    {type === 'CLIP' ? 'Clip (260,00 €)' : 'Komplette Brille (465,00 €)'}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="glassType">
              Glas {productType === 'COMPLETE_GLASSES' ? '*' : ''}
            </label>
            <select
              id="glassType"
              value={selectedGlass}
              onChange={e => setSelectedGlass(e.target.value)}
              required={productType === 'COMPLETE_GLASSES'}
              disabled={loading || productType === 'CLIP'}
            >
              <option value="">Bitte wählen...</option>
              {glassOptions.length === 0 ? (
                <option value="" disabled>
                  Lade Gläser...
                </option>
              ) : (
                glassOptions.map(opt => (
                  <option key={opt.id} value={opt.name}>
                    {opt.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="bandType">
              Band {productType === 'COMPLETE_GLASSES' ? '*' : ''}
            </label>
            <select
              id="bandType"
              value={selectedBand}
              onChange={e => setSelectedBand(e.target.value)}
              required={productType === 'COMPLETE_GLASSES'}
              disabled={loading || productType === 'CLIP'}
            >
              <option value="">Bitte wählen...</option>
              {bandOptions.length === 0 ? (
                <option value="" disabled>
                  Lade Bänder...
                </option>
              ) : (
                bandOptions.map(opt => (
                  <option key={opt.id} value={opt.name}>
                    {opt.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="form-group">
            <label>Lieferung *</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  value="SHOP"
                  checked={deliveryType === 'SHOP'}
                  onChange={e => {
                    setDeliveryType(e.target.value as 'SHOP');
                    setCustomerAddress('');
                  }}
                  disabled={loading}
                />
                An den Shop
              </label>
              <label>
                <input
                  type="radio"
                  value="CUSTOMER"
                  checked={deliveryType === 'CUSTOMER'}
                  onChange={e => setDeliveryType(e.target.value as 'CUSTOMER')}
                  disabled={loading}
                />
                Direkt an den Kunden
              </label>
            </div>
          </div>

          {deliveryType === 'CUSTOMER' && (
            <div className="form-group">
              <label htmlFor="customerAddress">Kundenadresse *</label>
              <textarea
                id="customerAddress"
                value={customerAddress}
                onChange={e => setCustomerAddress(e.target.value)}
                placeholder="Straße, PLZ Ort"
                required
                disabled={loading}
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="prescriptionImage">Rezeptbild(optional)</label>
            <input
              id="prescriptionImage"
              type="file"
              accept="image/*,.pdf"
              onChange={handleImageChange}
              disabled={loading}
            />
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Vorschau" />
              </div>
            )}
          </div>

          <div className="form-group">
            <h3 style={{ marginBottom: '1rem', fontSize: '1.1em', fontWeight: 'bold' }}>
              Brillenpass-Werte (optional)
            </h3>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                marginBottom: '1rem',
              }}
            >
              <div style={{ backgroundColor: 'lightblue', padding: '5px' }}>
                <h4 style={{ marginBottom: '0.5rem', fontSize: '1em' }}>Linkes Auge</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <label htmlFor="leftEyeSph" style={{ fontSize: '0.9em' }}>
                      Sph
                    </label>
                    <input
                      id="leftEyeSph"
                      type="number"
                      step="0.01"
                      value={leftEyePlano ? '0.00' : leftEyeSph}
                      onChange={e => setLeftEyeSph(e.target.value)}
                      placeholder="z.B. -2.50"
                      disabled={loading || leftEyePlano}
                      style={{ width: '100%', padding: '0.5rem' }}
                    />
                  </div>
                  <div>
                    <label htmlFor="leftEyeCyl" style={{ fontSize: '0.9em' }}>
                      Cyl
                    </label>
                    <input
                      id="leftEyeCyl"
                      type="number"
                      step="0.01"
                      value={leftEyePlano ? '0.00' : leftEyeCyl}
                      onChange={e => setLeftEyeCyl(e.target.value)}
                      placeholder="z.B. -0.75"
                      disabled={loading || leftEyePlano}
                      style={{ width: '100%', padding: '0.5rem' }}
                    />
                  </div>
                  <div>
                    <label htmlFor="leftEyeA" style={{ fontSize: '0.9em' }}>
                      A (Grad)
                    </label>
                    <input
                      id="leftEyeA"
                      type="number"
                      min="0"
                      max="180"
                      value={leftEyePlano ? '0' : leftEyeA}
                      onChange={e => setLeftEyeA(e.target.value)}
                      placeholder="0-180"
                      disabled={loading || leftEyePlano}
                      style={{ width: '100%', padding: '0.5rem' }}
                    />
                  </div>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={leftEyePlano}
                      onChange={e => {
                        const checked = e.target.checked;
                        setLeftEyePlano(checked);
                        if (checked) {
                          setLeftEyeSph('0.00');
                          setLeftEyeCyl('0.00');
                          setLeftEyeA('0');
                        }
                      }}
                      disabled={loading}
                    />
                    Plan (alle Werte null)
                  </label>
                </div>
              </div>

              <div style={{ backgroundColor: 'red', padding: '5px' }}>
                <h4 style={{ marginBottom: '0.5rem', fontSize: '1em' }}>Rechtes Auge</h4>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <label htmlFor="rightEyeSph" style={{ fontSize: '0.9em' }}>
                      Sph
                    </label>
                    <input
                      id="rightEyeSph"
                      type="number"
                      step="0.01"
                      value={rightEyePlano ? '0.00' : rightEyeSph}
                      onChange={e => setRightEyeSph(e.target.value)}
                      placeholder="z.B. -2.50"
                      disabled={loading || rightEyePlano}
                      style={{ width: '100%', padding: '0.5rem' }}
                    />
                  </div>
                  <div>
                    <label htmlFor="rightEyeCyl" style={{ fontSize: '0.9em' }}>
                      Cyl
                    </label>
                    <input
                      id="rightEyeCyl"
                      type="number"
                      step="0.01"
                      value={rightEyePlano ? '0.00' : rightEyeCyl}
                      onChange={e => setRightEyeCyl(e.target.value)}
                      placeholder="z.B. -0.75"
                      disabled={loading || rightEyePlano}
                      style={{ width: '100%', padding: '0.5rem' }}
                    />
                  </div>
                  <div>
                    <label htmlFor="rightEyeA" style={{ fontSize: '0.9em' }}>
                      A (Grad)
                    </label>
                    <input
                      id="rightEyeA"
                      type="number"
                      min="0"
                      max="180"
                      value={rightEyePlano ? '0' : rightEyeA}
                      onChange={e => setRightEyeA(e.target.value)}
                      placeholder="0-180"
                      disabled={loading || rightEyePlano}
                      style={{ width: '100%', padding: '0.5rem' }}
                    />
                  </div>
                  <label
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.35rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={rightEyePlano}
                      onChange={e => {
                        const checked = e.target.checked;
                        setRightEyePlano(checked);
                        if (checked) {
                          setRightEyeSph('0.00');
                          setRightEyeCyl('0.00');
                          setRightEyeA('0');
                        }
                      }}
                      disabled={loading}
                    />
                    Plan (alle Werte null)
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notizen (optional)</label>
            <textarea
              id="notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Zusätzliche Informationen zur Bestellung..."
              disabled={loading}
              maxLength={2000}
              rows={4}
            />
            <small>{notes.length} / 2000 Zeichen</small>
          </div>

          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}
          {orderNumber && (
            <div className="success">
              <strong>Bestellnummer: {orderNumber}</strong>
            </div>
          )}
          {priceInfo && (
            <div
              className="price-info"
              style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
              }}
            >
              <h3 style={{ margin: '0 0 0.5rem 0' }}>Preisübersicht:</h3>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '0.25rem',
                }}
              >
                <span>Basispreis:</span>
                <strong>{priceInfo.basePrice.toFixed(2)} €</strong>
              </div>
              {priceInfo.discountPercent > 0 && (
                <>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '0.25rem',
                      color: '#666',
                    }}
                  >
                    <span>Rabatt ({priceInfo.discountPercent}%):</span>
                    <span>
                      -{((priceInfo.basePrice * priceInfo.discountPercent) / 100).toFixed(2)} €
                    </span>
                  </div>
                  <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid #ddd' }} />
                </>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '0.5rem',
                  fontSize: '1.1em',
                }}
              >
                <strong>Endpreis:</strong>
                <strong style={{ color: '#2c3e50' }}>{priceInfo.finalPrice.toFixed(2)} €</strong>
              </div>
            </div>
          )}

          {deliveryTimes && (
            <div
              style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#e8f4f8',
                borderRadius: '4px',
                border: '1px solid var(--accent)',
              }}
            >
              <p style={{ margin: 0, fontWeight: 'bold', color: 'var(--accent)' }}>
                Geschätzte Lieferzeit:{' '}
                {productType === 'CLIP' ? deliveryTimes.CLIP : deliveryTimes.COMPLETE_GLASSES}{' '}
                Werktage
              </p>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ marginTop: '1rem' }}
          >
            {loading ? 'Wird erstellt...' : 'Bestellung aufgeben'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default OrderForm;
